import React, { useState, useEffect } from "react";
import Pagination from "../reusable/Pagination";
import ActionsDropdown from "../reusable/ActionsDropdown";
import { formatPhoneNumberForDisplay } from "../../utils/phoneUtils";
import {
  hasPermission,
  showToast,
} from "../../utils/utils";
import Loader from "../Live/Loader";
import { selectUserInformations } from "../../features/authSlice";
import { useSelector, useDispatch } from "react-redux";
import { API } from "../../shared/api/Api";
import { deleteStudent, selectStudentDeleteStatus, selectStudentDeleteError } from "../../features/students/studentSlice";
import { useNavigate } from "react-router-dom";
import { FiEdit, FiRefreshCw, FiActivity } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import Modal from "../Modal";
import EnrollStudent from "../Student/EnrollStudent";
import EnrollStudentsAdminFields from "../../config/Students/EnrollStudentsAdminFields";
import useStudentCrud from "../../hooks/CRUD/useStudentCrud";
import axios from "axios";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { saveAs } from 'file-saver';
import { ReactComponent as ExcelIcon } from '../../assets/svg/Excel.svg';

/**
 * StudentTable Component
 * Refactored to use navigation instead of modals (except for enroll which stays as modal)
 */
const StudentTable = ({
  students,
  currentPage,
  totalPages,
  onSearchChange,
  onPageChange,
  loader,
  setReFetchData,
  courseStudents = false,
  courseData = null,
  onSort,
  currentOrderBy,
  currentOrderDir,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  const deleteStatus = useSelector(selectStudentDeleteStatus);
  const deleteError = useSelector(selectStudentDeleteError);
  const canManageCourseEnrollment =
    hasPermission(user, "enroll_student") ||
    hasPermission(user, "enrollment_management") ||
    hasPermission(user, "delete_student");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [removeLoader, setRemoveLoader] = useState(false);

  // Initialize the useStudentCrud hook for enroll functionality
  const {
    formData,
    setFormData,
    loading,
    handleChange,
    formErrors,
    resetErrors,
  } = useStudentCrud(
    API.Students,
    EnrollStudentsAdminFields.reduce(
      (acc, field) => ({ ...acc, [field.name]: field.defaultValue || "" }),
      {}
    )
  );

  // Handle delete with confirmation
  const handleDelete = async (studentId) => {
    if (window.confirm("Are you sure you want to delete this student? There is no going back!!")) {
      const result = await dispatch(deleteStudent({ token: user.token, studentId }));

      if (deleteStudent.fulfilled.match(result)) {
        showToast('Success', 'Student deleted successfully', 'success');
        if (setReFetchData) {
          setReFetchData(true);
        }
      } else if (deleteStudent.rejected.match(result)) {
        showToast('Error', result.payload || 'Failed to delete student', 'error');
      }
    }
  };

  // Handle delete errors
  useEffect(() => {
    if (deleteStatus === 'failed' && deleteError) {
      showToast('Error', deleteError, 'error');
    }
  }, [deleteStatus, deleteError]);

  const handleEnrollStudentAdmin = async (action = "enroll", id = null) => {
    if (action === "remove") {
      const confirmed = window.confirm("Remove this student from this course only?");
      if (!confirmed) {
        return;
      }
    }

    try {
      setRemoveLoader(true);
      const { data } = await axios.post(
        `${action === "enroll"
          ? API.Enrollment.app_enrollment_enrollStudentToCourse
          : API.Enrollment.app_enrollment_removeStudentFromCourse
        }`,
        {
          student_id: action === "enroll" ? formData.student_id : id,
          course_id: courseData.course.id,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            lang: "en",
          },
        }
      );

      if (data.status === 200 && data.errorCode === 0) {
        showToast("Course Enrollment", data.message, "success");
        handleCloseModal();
      } else {
        showToast(
          "Oops, Something went wrong",
          data.message || "There is an error, please try again",
          "error"
        );
      }
    } catch (error) {
      showToast(
        "Oops, Something went wrong",
        error?.response?.data?.message || "There is an error, please try again",
        "error"
      );
    } finally {
      setRemoveLoader(false);
      if (setReFetchData) {
        setReFetchData(true);
      }
    }
  };

  // Handle form submission for enrollment
  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleEnrollStudentAdmin();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActionType(null);
    setSelectedStudentId(null);
    setFormData(
      EnrollStudentsAdminFields.reduce(
        (acc, field) => ({ ...acc, [field.name]: field.defaultValue || "" }),
        {}
      )
    );
    resetErrors();
  };

  // Handle opening modal (only for enroll)
  const handleOpenModal = (type, id = null) => {
    setActionType(type);
    setSelectedStudentId(id);
    setIsModalOpen(true);
  };

  // Excel export handler
  const exportToExcel = async () => {
    try {
      const response = await axios.get(API.Students.app_students_export_to_excel, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      let filename;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('filename=') !== -1) {
        filename = disposition.split('filename=')[1].trim().replace(/['"]/g, '');
      } else {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, '0');
        const formatted = `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`;
        filename = `students_${formatted}.xlsx`;
      }
      saveAs(response.data, filename);
      showToast('Success', 'Students exported successfully', 'success');
    } catch (error) {
      showToast(
        'Export Failed',
        error?.response?.data?.message || 'Could not export students. Please try again.',
        'error'
      );
    }
  };

  // Column definitions
  const columns = [
    { id: "id", label: "ID", sortable: true },
    { id: "first_name", label: "First Name", sortable: true },
    { id: "last_name", label: "Last Name", sortable: true },
    { id: "mobile", label: "Mobile", sortable: true },
    { id: "nationalId", label: "National ID", sortable: true },
    { id: "country", label: "Country", sortable: true },
    { id: "city", label: "City", sortable: true },
    { id: "created_at", label: "Created At", sortable: true },
  ];

  // Handle sorting
  const handleSortClick = (columnId) => {
    if (currentOrderBy === columnId) {
      const newDirection = currentOrderDir === "ASC" ? "DESC" : "ASC";
      onSort(columnId, newDirection);
    } else {
      onSort(columnId, "ASC");
    }
  };

  // Render sort indicator
  const renderSortIndicator = (columnId) => {
    const isActive = currentOrderBy === columnId;

    return (
      <div className="inline-flex flex-col ml-1">
        <FaSortUp
          className={`${isActive && currentOrderDir === "ASC" ? "text-primary" : "text-gray-400"} -mb-1`}
          size={12}
        />
        <FaSortDown
          className={`${isActive && currentOrderDir === "DESC" ? "text-primary" : "text-gray-400"} -mt-1`}
          size={12}
        />
      </div>
    );
  };

  const getDropdownActions = (id) => {
    const actions = [];

    if (hasPermission(user, "view_student")) {
      actions.push({
        icon: (
          <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ),
        label: "Preview",
        onClick: () => navigate(`/dashboard/students/${id}`),
      });
    }

    if (hasPermission(user, "update_student")) {
      actions.push({
        icon: <FiEdit className="w-4 h-4" />,
        label: "Edit",
        onClick: () => navigate(`/dashboard/students/${id}/edit`),
      });
    }

    if (!courseStudents && hasPermission(user, "delete_student")) {
      actions.push({
        icon: <MdDeleteOutline className="w-4 h-4" />,
        label: "Delete",
        onClick: () => handleDelete(id),
      });
    }

    return actions;
  };

  const renderModalContent = () => {
    if (actionType === "enroll") {
      return (
        <EnrollStudent
          course={courseData.course}
          entityFields={EnrollStudentsAdminFields}
          formData={formData}
          formErrors={formErrors}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          loading={loading || removeLoader}
          onClose={handleCloseModal}
        />
      );
    }
    return null;
  };

  return (
    <div className="w-full mx-auto mt-8 px-2 sm:px-4 lg:px-0">
      <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-4 sm:p-6 lg:p-8 relative">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-anek-odia text-2xl sm:text-3xl font-semibold text-gray-900">
              {courseStudents ? "Course Students" : "Students"}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              View and manage student records, enrollment, and contact details.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {!courseStudents && hasPermission(user, "view_student") && (
              <button
                type="button"
                onClick={() => navigate('/dashboard/students/history')}
                className="inline-flex items-center justify-center gap-2 text-white bg-purple-500 hover:bg-purple-600 rounded-full px-4 h-10 sm:h-11 text-xs sm:text-sm font-medium transition-colors"
                title="Student History Search"
              >
                <FiActivity className="w-4 h-4" />
                <span className="hidden sm:inline">History</span>
              </button>
            )}
            {!courseStudents && hasPermission(user, "create_student") && (
              <button
                type="button"
                onClick={() => navigate('/dashboard/students/create')}
                className="inline-flex items-center justify-center gap-2 text-white bg-primary rounded-full px-3 sm:px-4 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-primary-hover transition-colors"
                title="Add Student"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <svg
                    className="w-3.5 h-3.5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 18 18"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 1v16M1 9h16"
                    />
                  </svg>
                </span>
                <span className="hidden sm:inline">New Student</span>
              </button>
            )}
            {courseStudents && canManageCourseEnrollment && (
              <button
                type="button"
                onClick={() => handleOpenModal("enroll")}
                className="inline-flex items-center justify-center gap-2 text-white bg-primary rounded-full px-3 sm:px-4 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-primary-hover transition-colors"
                title="Enroll Student"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <svg
                    className="w-3.5 h-3.5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 18 18"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 1v16M1 9h16"
                    />
                  </svg>
                </span>
                <span className="hidden sm:inline">Enroll</span>
              </button>
            )}
            {!courseStudents && hasPermission(user, "view_student") && (
              <button
                type="button"
                onClick={exportToExcel}
                className="inline-flex items-center justify-center gap-2 text-white bg-emerald-500 hover:bg-emerald-600 rounded-full px-3 h-10 sm:h-11 text-xs sm:text-sm font-medium transition-colors"
                title="Export to Excel"
              >
                <ExcelIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setReFetchData && setReFetchData(true)}
              className="inline-flex items-center justify-center gap-2 text-gray-700 bg-white border border-gray-200 rounded-full px-3 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors"
              title="Refresh Data"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Header: Search */}
        <div className="mt-5 flex justify-end items-center border-t border-gray-100 pt-4">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search students..."
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-full border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary transition"
            />
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M11 5a6 6 0 100 12 6 6 0 000-12z"
                />
              </svg>
            </span>
          </div>
        </div>

        {/* Table Container */}
        <div className="mt-6 rounded-2xl border border-gray-100 bg-white overflow-hidden">
          <div className="w-full overflow-x-auto">
            <div className="min-w-[900px]">
              {loader && (
                <div className="flex justify-center items-center py-6">
                  <Loader />
                </div>
              )}

              <table className="w-full table-auto">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.id}
                        className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide ${currentOrderBy === column.id
                            ? "text-primary"
                            : "text-gray-500"
                          } border-b border-gray-100 ${column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                          }`}
                        onClick={
                          column.sortable
                            ? () => handleSortClick(column.id)
                            : undefined
                        }
                      >
                        <div className="flex items-center gap-1">
                          {column.label}
                          {column.sortable && renderSortIndicator(column.id)}
                        </div>
                      </th>
                    ))}

                    {(hasPermission(user, "update_student") ||
                      hasPermission(user, "delete_student") ||
                      hasPermission(user, "view_student") ||
                      (courseStudents && canManageCourseEnrollment)) && (
                        <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                          Actions
                        </th>
                      )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {students.length === 0 && !loader ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="px-4 sm:px-6 py-10 text-center text-gray-500"
                      >
                        No students found
                      </td>
                    </tr>
                  ) : (
                    students.map((student, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors cursor-auto text-sm"
                      >
                        <td className="px-4 sm:px-6 py-4 text-gray-900 font-medium">{student.id || '-'}</td>
                        <td className="px-4 sm:px-6 py-4 text-gray-900 font-medium">{student.first_name || '-'}</td>
                        <td className="px-4 sm:px-6 py-4 text-gray-700">{student.last_name || '-'}</td>
                        <td className="px-4 sm:px-6 py-4 text-gray-700">
                          {student.mobile ? formatPhoneNumberForDisplay(student.mobile) : '-'}
                        </td>
                        <td className="px-4 sm:px-6 py-4 text-gray-700">{student.nationalId || '-'}</td>
                        <td className="px-4 sm:px-6 py-4 text-gray-700">{student.country || '-'}</td>
                        <td className="px-4 sm:px-6 py-4 text-gray-700">{student.city || '-'}</td>
                        <td className="px-4 sm:px-6 py-4 text-gray-700">{student.created_at || '-'}</td>
                        {(hasPermission(user, "update_student") ||
                          hasPermission(user, "delete_student") ||
                          hasPermission(user, "view_student") ||
                          (courseStudents && canManageCourseEnrollment)) && (
                            <td className="px-4 sm:px-6 py-4 text-right">
                              <ActionsDropdown
                                actions={courseStudents
                                  ? [
                                    ...getDropdownActions(student.id),
                                    ...(canManageCourseEnrollment ? [{
                                      icon: <MdDeleteOutline className="w-4 h-4" />,
                                      label: "Remove from Course",
                                      onClick: () => handleEnrollStudentAdmin("remove", student.id),
                                    }] : [])
                                  ]
                                  : getDropdownActions(student.id)
                                }
                              />
                            </td>
                          )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <p className="hidden sm:block text-xs text-gray-500">
            Showing page <span className="font-semibold">{currentPage}</span> of{" "}
            <span className="font-semibold">{totalPages}</span>
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>

        {/* Modal for enrollment only */}
        {courseStudents && (
          <Modal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            width="max-w-7xl"
            withHeader={false}
            withButtons={false}
          >
            {renderModalContent()}
          </Modal>
        )}
      </div>
    </div>
  );
};

export default StudentTable;
